/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { Scale } from '../utils/UNSAFE_visTypes/chart';
import { styles as chartStyles } from '#PRIVATE_Chart/themes/ChartStyles.css';
import { classNames } from '../utils/UNSAFE_classNames';

type ConstantAreaRefObjProps = {
  id: string;
  xScale: Scale;
  yScale: Scale;
  low: number;
  high?: number;
  orientation?: 'horizontal' | 'vertical';
  areaColor?: string;
  accessibleLabel?: string;
  activeId?: string;
  isXRef?: boolean;
};

export function ConstantAreaRefObj({
  id,
  xScale,
  yScale,
  low,
  high,
  orientation = 'horizontal',
  areaColor,
  accessibleLabel,
  isXRef,
  activeId
}: ConstantAreaRefObjProps) {
  if (!xScale || !yScale) {
    return null;
  }

  const targetScale = isXRef ? xScale : yScale;
  const spanningScale = isXRef ? yScale : xScale;
  const domain = targetScale.domain();
  const clampedLow =
    low !== undefined
      ? Math.max(low, Math.min(domain[0], domain[1]))
      : Math.min(domain[0], domain[1]);
  const clampedHigh =
    high !== undefined
      ? Math.min(high, Math.max(domain[0], domain[1]))
      : Math.max(domain[0], domain[1]);

  const pos1 = targetScale.transform(clampedLow);
  const pos2 = targetScale.transform(clampedHigh);
  const [span1, span2] = spanningScale.range();

  if (typeof pos1 !== 'number' || typeof pos2 !== 'number' || isNaN(pos1) || isNaN(pos2)) {
    return null;
  }

  // Determine if we're drawing horizontal or vertical area
  const isHorizontalArea =
    (orientation === 'vertical' && !isXRef) || (orientation === 'horizontal' && isXRef);

  const rectProps = isHorizontalArea
    ? {
        x: Math.min(span1, span2),
        y: Math.min(pos1, pos2),
        width: Math.abs(span2 - span1),
        height: Math.max(1, Math.abs(pos2 - pos1))
      }
    : {
        x: Math.min(pos1, pos2),
        y: Math.min(span1, span2),
        width: Math.max(1, Math.abs(pos2 - pos1)),
        height: Math.abs(span2 - span1)
      };

  return (
    <rect
      {...rectProps}
      id={activeId}
      aria-label={accessibleLabel}
      data-oj-private-ref-obj-id={id}
      data-oj-private-object={'refArea'}
      role="img"
      className={classNames([chartStyles.refArea])}
      style={{
        fill: areaColor
      }}
    />
  );
}
